import { on } from '@ember/modifier';
import { or } from 'tio-ui/utilities';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import type Owner from '@ember/owner';
import { PhCaretDown } from 'ember-phosphor-icons';

interface VStackSignature {
  Args: {
    collapsed?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: { default: [] };
}

export const VStack: TOC<VStackSignature> = <template>
  <div data-stack-collapsed={{if @collapsed "true" "false"}} data-vstack ...attributes>
    {{yield}}
  </div>
</template>;

interface HStackSignature {
  Args: {
    collapsed?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: { default: [] };
}

export const HStack: TOC<HStackSignature> = <template>
  <div data-stack-collapsed={{if @collapsed "true" "false"}} data-hstack ...attributes>
    {{yield}}
  </div>
</template>;

interface HeaderSignature {
  Args: Record<string, never>;
  Element: HTMLElement;
  Blocks: { default: [] };
}

export const Header: TOC<HeaderSignature> = <template>
  <header class="tio-text-brand" data-header ...attributes>
    {{yield}}
  </header>
</template>;

interface SectionSignature {
  Args: {
    headerClass?: string;
    collapsed?: boolean;
  };
  Element: HTMLElement;
  Blocks: { default: []; header: []; subHeader: []; actions: []; body: [] };
}

export class Section extends Component<SectionSignature> {
  @tracked isOpen = true;

  constructor(owner: Owner, args: SectionSignature['Args']) {
    super(owner, args);
    if (args.collapsed) {
      this.isOpen = !args.collapsed;
    }
  }

  get isCollapsible() {
    return this.args.collapsed !== undefined;
  }

  get isClosed() {
    return !this.isOpen;
  }

  toggle = () => {
    this.isOpen = !this.isOpen;
  };

  <template>
    <section
      data-section
      data-open={{if this.isOpen "true" "false"}}
      data-has-body={{if (has-block "body") "true" "false"}}
      ...attributes
    >
      {{#if
        (or this.isCollapsible (has-block "header") (has-block "subHeader") (has-block "actions"))
      }}
        <div data-section-header-row>
          {{#if this.isCollapsible}}
            <button
              type="button"
              class="group h-6"
              {{on "click" this.toggle}}
              data-section-caret-trigger
            >
              <PhCaretDown
                class="w-4 h-4 tio-fg-info group-hover:tio-fg-info-body transition-transform duration-200 ease-out will-change-transform
                  {{if this.isOpen 'rotate-0' '-rotate-90'}}"
                data-section-caret
              />
            </button>
          {{/if}}
          <div data-section-header-inner>
            <div data-section-header-text>
              {{#if (has-block "header")}}
                <h2 class="font-semibold {{@headerClass}}" data-section-header>
                  {{yield to="header"}}
                </h2>
              {{/if}}
              {{#if (has-block "subHeader")}}
                {{#if (has-block "header")}}
                  <h3 class="text-sm" data-section-sub-header>
                    {{yield to="subHeader"}}
                  </h3>
                {{else}}
                  <h2 class="text-sm" data-section-sub-header>
                    {{yield to="subHeader"}}
                  </h2>
                {{/if}}
              {{/if}}
            </div>
            {{#if (has-block "actions")}}
              <div class="flex items-end gap-2" data-section-actions>
                {{yield to="actions"}}
              </div>
            {{/if}}
          </div>
        </div>
        {{#if this.isOpen}}
          <div data-section-divider></div>
        {{/if}}
      {{/if}}

      {{#if (has-block "body")}}
        <div hidden={{this.isClosed}} data-section-body-wrapper data-section-body>
          {{yield to="body"}}
        </div>
      {{/if}}
      {{yield}}
    </section>
  </template>
}

export type { VStackSignature, HStackSignature, HeaderSignature, SectionSignature };
